import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  FileText, CheckCircle, AlertTriangle, Loader2, Send,
  ExternalLink, CreditCard, Receipt, ChevronDown, ChevronUp, Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INV_STATUS_COLORS = {
  draft:          "bg-slate-100 text-slate-600",
  sent:           "bg-blue-100 text-blue-800",
  partially_paid: "bg-amber-100 text-amber-800",
  paid:           "bg-emerald-100 text-emerald-800",
  overdue:        "bg-red-100 text-red-800",
  cancelled:      "bg-red-50 text-red-400",
};

const PAY_STATUS_COLORS = {
  not_requested:   "bg-slate-100 text-slate-500",
  payment_pending: "bg-amber-100 text-amber-800",
  partially_paid:  "bg-blue-100 text-blue-800",
  paid:            "bg-emerald-100 text-emerald-800",
  refunded:        "bg-purple-100 text-purple-800",
};

export default function BookingInvoicePanel({ booking, onUpdate, onInvoiceFound }) {
  const navigate   = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [invoice,         setInvoice]         = useState(null);
  const [loadingInv,      setLoadingInv]       = useState(true);
  const [creating,        setCreating]         = useState(false);
  const [sendingEmail,    setSendingEmail]     = useState(null); // null | "test" | "customer"
  const [emailPreview,    setEmailPreview]     = useState(false);
  const [receipts,        setReceipts]         = useState([]);
  const [showReceipts,    setShowReceipts]     = useState(false);

  // Record payment form
  const [showPayDialog,   setShowPayDialog]    = useState(false);
  const [payForm,         setPayForm]          = useState({
    amount: "", paid_date: new Date().toISOString().split("T")[0],
    payment_method: "bank_transfer", payment_reference: "",
  });
  const [recordingPay,    setRecordingPay]     = useState(false);

  const fetchLinked = useCallback(async () => {
    if (!booking?.id) return;
    setLoadingInv(true);
    try {
      const res = await axios.get(
        `${API}/invoices?source_type=flight_booking&source_id=${booking.id}`,
        { headers: getAuthHeaders() },
      );
      const found = (res.data || []).find(i => i.invoice_status !== "cancelled") || null;
      setInvoice(found);
      onInvoiceFound?.(found);
      if (found) {
        const rRes = await axios.get(
          `${API}/invoices/${found.id}/receipts`,
          { headers: getAuthHeaders() },
        );
        setReceipts(rRes.data || []);
      } else {
        setReceipts([]);
      }
    } catch {}
    finally { setLoadingInv(false); }
  }, [booking?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLinked(); }, [fetchLinked]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/invoices/from-source`,
        { source_type: "flight_booking", source_id: booking.id },
        { headers: getAuthHeaders() },
      );
      setInvoice(res.data);
      toast.success(`Invoice created — ${res.data.invoice_number}`);
      onUpdate?.();
    } catch (e) {
      if (e.response?.status === 409) {
        const existing = e.response.data?.detail?.invoice || e.response.data?.invoice;
        if (existing) {
          setInvoice(existing);
          toast.info(`Invoice already exists — ${existing.invoice_number}`);
          return;
        }
      }
      toast.error(e.response?.data?.detail || "Failed to create invoice");
    } finally { setCreating(false); }
  };

  const handleSendTest = async () => {
    if (!invoice) return;
    setSendingEmail("test");
    try {
      const res = await axios.post(
        `${API}/invoices/${invoice.id}/send-email`,
        { is_test: true },
        { headers: getAuthHeaders() },
      );
      toast.success(res.data.message || "Test invoice sent to admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send test email");
    } finally { setSendingEmail(null); }
  };

  const confirmSendCustomer = async () => {
    setSendingEmail("customer");
    setEmailPreview(false);
    try {
      const res = await axios.post(
        `${API}/invoices/${invoice.id}/send-email`,
        { is_test: false, recipient_email: invoice.customer_email || undefined },
        { headers: getAuthHeaders() },
      );
      toast.success(res.data.message || "Invoice sent to customer");
      setInvoice(i => i ? { ...i, invoice_sent_at: new Date().toISOString(), invoice_sent_to: invoice.customer_email } : i);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invoice");
    } finally { setSendingEmail(null); }
  };

  const handleRecordPayment = async () => {
    if (!invoice) return;
    const amt = parseFloat(payForm.amount);
    if (!payForm.amount || isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setRecordingPay(true);
    try {
      await axios.post(
        `${API}/invoices/${invoice.id}/record-payment`,
        {
          paid_amount:       amt,
          paid_date:         payForm.paid_date,
          payment_method:    payForm.payment_method,
          payment_reference: payForm.payment_reference || undefined,
        },
        { headers: getAuthHeaders() },
      );
      toast.success("Payment recorded");
      setShowPayDialog(false);
      setPayForm({ amount: "", paid_date: new Date().toISOString().split("T")[0], payment_method: "bank_transfer", payment_reference: "" });
      await fetchLinked();
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally { setRecordingPay(false); }
  };

  // ─── Loading skeleton ──────────────────────────────────────────────────────
  if (loadingInv) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-slate-400">
            <FileText className="w-5 h-5" /> Invoice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-300" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── No invoice yet ────────────────────────────────────────────────────────
  if (!invoice) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" /> Invoice
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {booking.ticket_email_sent && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">No invoice has been sent for this booking</p>
                <p className="text-xs text-amber-600 mt-0.5">E-ticket was sent. Create an invoice to complete the financial record.</p>
              </div>
            </div>
          )}
          <p className="text-sm text-slate-500">No customer invoice linked to this booking.</p>
          <Button onClick={handleCreate} disabled={creating} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
            {creating
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>
              : <><FileText className="w-4 h-4 mr-2" />Create Invoice</>}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ─── Invoice exists ────────────────────────────────────────────────────────
  const balanceDue = parseFloat(invoice.balance_due ?? 0);
  const total      = parseFloat(invoice.invoice_total ?? 0);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" /> Invoice
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Summary row */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-mono text-sm font-bold text-slate-800">{invoice.invoice_number}</p>
              <p className="text-xs text-slate-500 mt-0.5 truncate max-w-[140px]">{invoice.customer_name}</p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Badge className={INV_STATUS_COLORS[invoice.invoice_status] || "bg-slate-100 text-slate-600"}>
                {invoice.invoice_status}
              </Badge>
              {invoice.payment_status && invoice.payment_status !== "not_requested" && (
                <Badge className={`text-xs ${PAY_STATUS_COLORS[invoice.payment_status] || "bg-slate-100 text-slate-500"}`}>
                  {invoice.payment_status.replace(/_/g, " ")}
                </Badge>
              )}
            </div>
          </div>

          {/* Amounts */}
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Total</span>
              <span className="font-mono font-medium">€{total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Balance Due</span>
              <span className={`font-mono font-medium ${balanceDue > 0.005 ? "text-amber-600" : "text-emerald-600"}`}>
                €{balanceDue.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Sent indicator */}
          {invoice.invoice_sent_at && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              Sent {new Date(invoice.invoice_sent_at).toLocaleDateString("en-NL", { day: "numeric", month: "short", year: "numeric" })}
              {invoice.invoice_sent_to && <> to <span className="truncate">{invoice.invoice_sent_to}</span></>}
            </div>
          )}

          {/* Actions */}
          <div className="border-t pt-3 space-y-2">
            <Button variant="outline" className="w-full justify-start h-9 text-sm"
              onClick={() => navigate(`/invoices/${invoice.id}`)}>
              <ExternalLink className="w-3.5 h-3.5 mr-2" />Open Invoice
            </Button>

            <Button variant="outline"
              className="w-full justify-start h-9 text-sm text-slate-600 hover:bg-slate-50"
              onClick={handleSendTest}
              disabled={!!sendingEmail}>
              {sendingEmail === "test"
                ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Sending...</>
                : <><Send className="w-3.5 h-3.5 mr-2" />Send Test Invoice → Admin</>}
            </Button>

            <Button
              className="w-full justify-start h-9 text-sm bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setEmailPreview(true)}
              disabled={!!sendingEmail}>
              {sendingEmail === "customer"
                ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Sending...</>
                : <><Send className="w-3.5 h-3.5 mr-2" />Send Invoice to Customer</>}
            </Button>

            {balanceDue > 0.005 && (
              <Button variant="outline"
                className="w-full justify-start h-9 text-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={() => {
                  setPayForm(f => ({ ...f, amount: balanceDue.toFixed(2) }));
                  setShowPayDialog(true);
                }}>
                <CreditCard className="w-3.5 h-3.5 mr-2" />Record Payment
              </Button>
            )}

            {receipts.length > 0 && (
              <>
                <Button variant="ghost"
                  className="w-full justify-start h-9 text-sm text-slate-500 hover:text-slate-700"
                  onClick={() => setShowReceipts(s => !s)}>
                  <Receipt className="w-3.5 h-3.5 mr-2" />
                  View Receipts ({receipts.length})
                  {showReceipts
                    ? <ChevronUp className="w-3.5 h-3.5 ml-auto" />
                    : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
                </Button>
                {showReceipts && (
                  <div className="space-y-1.5 pl-1">
                    {receipts.map(r => (
                      <div key={r.id}
                        className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border border-slate-100 text-xs cursor-pointer hover:bg-slate-100 transition-colors"
                        onClick={() => navigate(`/receipts/${r.id}`)}>
                        <span className="font-mono font-medium text-slate-700">{r.receipt_number}</span>
                        <span className="text-slate-500">€{parseFloat(r.amount_received ?? 0).toFixed(2)}</span>
                        <span className="text-slate-400">{r.payment_date}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Send to customer — preview dialog ─────────────────────────────── */}
      <Dialog open={emailPreview} onOpenChange={v => !v && setEmailPreview(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-600" />
              Confirm: Send Invoice
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Recipient</span>
                <span className="font-medium truncate max-w-[180px]">
                  {invoice?.customer_email || invoice?.customer_name || "—"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Invoice No.</span>
                <span className="font-mono font-medium">{invoice?.invoice_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Amount Due</span>
                <span className="font-mono font-medium">€{balanceDue.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Attachment</span>
                <span className="text-slate-600">{invoice?.invoice_number}.pdf</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Mode</span>
                <Badge className="bg-blue-100 text-blue-800 text-xs">Customer</Badge>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEmailPreview(false)}>Cancel</Button>
            <Button onClick={confirmSendCustomer} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Send className="w-4 h-4 mr-2" />Confirm & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Record Payment dialog ──────────────────────────────────────────── */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-emerald-600" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Amount (€) <span className="text-slate-400 font-normal">— balance due: €{balanceDue.toFixed(2)}</span></Label>
              <Input
                type="number" step="0.01" min="0.01"
                value={payForm.amount}
                onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                placeholder={`0.00`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Payment Date</Label>
              <Input
                type="date"
                value={payForm.paid_date}
                onChange={e => setPayForm(f => ({ ...f, paid_date: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Method</Label>
              <select
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-slate-400"
                value={payForm.payment_method}
                onChange={e => setPayForm(f => ({ ...f, payment_method: e.target.value }))}
              >
                <option value="bank_transfer">Bank Transfer</option>
                <option value="tikkie">Tikkie</option>
                <option value="stripe">Stripe</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Reference (optional)</Label>
              <Input
                value={payForm.payment_reference}
                onChange={e => setPayForm(f => ({ ...f, payment_reference: e.target.value }))}
                placeholder="Transaction ID or description"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowPayDialog(false)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={recordingPay}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {recordingPay
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Recording...</>
                : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
